import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { useMigrationDetailQuery } from './hooks/useMigrationQuery'

/**
 * Port of `app/migration/migration-detail.html` +
 * `app/migration/migrationDetailController.js` (AngularJS
 * `migrationDetailController`) — the "Entidades inválidas" screen for one
 * migration namespace, reached at `/workspace/migration/:namespace` (legacy
 * state `workspace.migration-details`, URL `/workspace/migration/{namespace}`;
 * see `AppRoutes.jsx`).
 *
 * Legacy behavior ported 1:1 (backed by `useMigrationDetailQuery` in
 * `./hooks/useMigrationQuery.js`):
 * - `$stateParams.namespace` -> React Router's `useParams().namespace`.
 * - `data-ng-init="initView()"` fetches the invalid-entity list
 *   unconditionally on mount (`MigrationService.getMigrationDetail`) ->
 *   `useMigrationDetailQuery(namespace)`, a plain `useQuery` enabled
 *   whenever `namespace` is present (no manual trigger needed).
 * - Heading "Entidades inválidas: {{namespace}} ({{entities.length}})".
 * - "Voltar" link (`ng-click="back()"` — `back()` isn't actually defined
 *   anywhere in this controller, only inherited from the workspace/main
 *   scope in the legacy app, and simply pops browser history) -> a plain
 *   `navigate(-1)`, the direct equivalent.
 * - One row per entity: `migrationEntityId`, `dataId`, `statusReport`
 *   (rendered in a `<pre>`, preserved here via MUI's `component="pre"`
 *   so whitespace/newlines in the report still render as authored) and an
 *   "Editar JSON" action.
 * - "Editar JSON" (`viewJson(entity)`): legacy does
 *   `$state.go('workspace.migration-json', {entity: JSON.stringify(entity)})`
 *   — the *entire* entity object round-trips through the next screen's
 *   route param, not just an id. Reproduced here by navigating to
 *   `/workspace/migration/json/:entity` with `entity` set to
 *   `encodeURIComponent(JSON.stringify(entity))`; React Router already
 *   URL-decodes route params for us, so `MigrationJsonPage` only needs to
 *   `JSON.parse` what `useParams().entity` gives it. `encodeURIComponent`
 *   is added on top of what the legacy `$state.go` did implicitly (UI-Router
 *   encodes path params for its own hash-based URLs) because React Router's
 *   browser history needs a literal path segment with no raw `{`/`"`/`/`
 *   characters, which raw `JSON.stringify` output would otherwise contain.
 * - Loading indicator -> MUI `LinearProgress`, matching every other ported
 *   listing screen (`HistoriesPage`, `ClippingsListPage`, `MigrationListPage`).
 * - Fetch errors (not handled at all in the legacy controller, which only
 *   `console.log`s them) -> a MUI `Alert`, so a failed fetch isn't silently
 *   blank on screen; this is a strict improvement over "nothing visibly
 *   happens", not a behavior change users could rely on.
 *
 * MUI `Table` (not `DataGrid`) is used here for the same reason documented
 * in `MigrationListPage.jsx`: no pagination/sorting/filtering exists on this
 * screen to justify pulling in `@mui/x-data-grid`.
 */
function MigrationDetailPage() {
  const { namespace } = useParams()
  const navigate = useNavigate()
  const detailQuery = useMigrationDetailQuery(namespace)

  const entities = detailQuery.data || []
  const isLoading = detailQuery.isPending

  /** Port of `viewJson(entity)` — see doc-comment above. */
  function handleViewJson(entity) {
    navigate(`/workspace/migration/json/${encodeURIComponent(JSON.stringify(entity))}`)
  }

  function handleBack() {
    navigate(-1)
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Entidades inválidas: {namespace} ({entities.length})
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Link component="button" type="button" onClick={handleBack}>
          Voltar
        </Link>
      </Box>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {detailQuery.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar as entidades inválidas deste namespace.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Id no Astrea</TableCell>
              <TableCell>Id</TableCell>
              <TableCell>Relatório</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.map((entity, index) => (
              <TableRow key={`${entity.migrationEntityId}-${index}`}>
                <TableCell>{entity.migrationEntityId}</TableCell>
                <TableCell>{entity.dataId}</TableCell>
                <TableCell>
                  <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                    {entity.statusReport}
                  </Box>
                </TableCell>
                <TableCell>
                  <Link component="button" type="button" onClick={() => handleViewJson(entity)}>
                    Editar JSON
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default MigrationDetailPage
